/**
 * Routes Layer - Visualización de rutas de recolección en el mapa
 *
 * @version 1.0
 * @module core/modules/waste/map-layers
 *
 * @description
 * Gestiona la capa de rutas en el mapa usando MapLibre GL.
 * Incluye visualización de líneas, flechas direccionales, labels y estados.
 *
 * @example
 * ```typescript
 * import { initializeRoutesLayer, updateRoutesData } from './routes-layer';
 *
 * // Inicializar capa
 * initializeRoutesLayer(map, projectId);
 *
 * // Actualizar datos
 * updateRoutesData(routes);
 *
 * // Highlight ruta
 * highlightRoute(routeId);
 * ```
 */

import type { Map as MapLibreMap, GeoJSONSource } from 'maplibre-gl';
import type { FeatureCollection, Feature, LineString } from 'geojson';
import type { WasteRoute } from '../types';

/**
 * IDs de sources y layers
 */
const SOURCE_ID = 'waste-routes-source';
const LAYER_HALO_ID = 'waste-routes-halo';
const LAYER_LINE_ID = 'waste-routes-lines';
const LAYER_ARROWS_ID = 'waste-routes-arrows';
const LAYER_LABELS_ID = 'waste-routes-labels';

/**
 * Colores por estado de ruta
 */
const ROUTE_COLORS = {
  active: '#2FA4B5',
  paused: '#F59E0B',
  discontinued: '#6B7280',
} as const;

/**
 * Estado del layer
 */
let mapInstance: MapLibreMap | null = null;
let currentProjectId: string | null = null;
let selectedRouteId: string | null = null;

/**
 * Convierte rutas a FeatureCollection
 *
 * @param routes - Array de rutas
 * @returns FeatureCollection para GeoJSON source
 */
function routesToFeatureCollection(routes: WasteRoute[]): FeatureCollection<LineString> {
  const features: Feature<LineString>[] = routes.map((route) => ({
    type: 'Feature',
    id: route.id,
    geometry: route.geometry,
    properties: {
      id: route.id,
      code: route.code,
      name: route.name,
      status: route.status,
      color: ROUTE_COLORS[route.status],
      containerCount: route.containerIds.length,
      distance: route.estimatedDistance,
    },
  }));

  return {
    type: 'FeatureCollection',
    features,
  };
}

/**
 * Inicializa la capa de rutas en el mapa
 *
 * @param map - Instancia de MapLibre
 * @param projectId - ID del proyecto
 *
 * @remarks
 * Crea la source y todas las layers necesarias.
 * Solo debe llamarse una vez por mapa.
 */
export function initializeRoutesLayer(map: MapLibreMap, projectId: string): void {
  mapInstance = map;
  currentProjectId = projectId;

  console.log('[RoutesLayer] Initializing for project:', projectId);

  // Verificar que el mapa esté cargado
  if (!map.isStyleLoaded()) {
    map.on('load', () => initializeRoutesLayer(map, projectId));
    return;
  }

  // Eliminar layers existentes (si los hay)
  if (map.getLayer(LAYER_LABELS_ID)) map.removeLayer(LAYER_LABELS_ID);
  if (map.getLayer(LAYER_ARROWS_ID)) map.removeLayer(LAYER_ARROWS_ID);
  if (map.getLayer(LAYER_LINE_ID)) map.removeLayer(LAYER_LINE_ID);
  if (map.getLayer(LAYER_HALO_ID)) map.removeLayer(LAYER_HALO_ID);
  if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);

  // Crear source vacía
  map.addSource(SOURCE_ID, {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  });

  // Layer 1: Halo blanco (sombra cuando está seleccionada)
  map.addLayer({
    id: LAYER_HALO_ID,
    type: 'line',
    source: SOURCE_ID,
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
    paint: {
      'line-color': '#ffffff',
      'line-width': [
        'case',
        ['boolean', ['feature-state', 'selected'], false],
        8, // Ancho del halo cuando está seleccionada
        0, // Invisible cuando no está seleccionada
      ],
      'line-opacity': 0.6,
      'line-blur': 2, // Difuminar el halo para efecto de sombra
    },
  });

  // Layer 2: Líneas de rutas (encima del halo)
  map.addLayer({
    id: LAYER_LINE_ID,
    type: 'line',
    source: SOURCE_ID,
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
    paint: {
      'line-color': ['get', 'color'],
      'line-width': 3, // Grosor constante (no cambia con selección)
      'line-opacity': [
        'case',
        ['boolean', ['feature-state', 'selected'], false],
        1.0, // Más opaca cuando está seleccionada
        0.8, // Opacidad normal
      ],
    },
  });

  // Layer 3: Flechas direccionales
  map.addLayer({
    id: LAYER_ARROWS_ID,
    type: 'symbol',
    source: SOURCE_ID,
    layout: {
      'symbol-placement': 'line',
      'symbol-spacing': 100,
      'text-field': '→',
      'text-size': 16,
      'text-keep-upright': false,
      'text-rotation-alignment': 'map',
    },
    paint: {
      'text-color': ['get', 'color'],
      'text-opacity': 0.7,
    },
  });

  // Layer 4: Labels con código
  map.addLayer({
    id: LAYER_LABELS_ID,
    type: 'symbol',
    source: SOURCE_ID,
    layout: {
      'text-field': ['get', 'code'],
      'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
      'text-size': 11,
      'symbol-placement': 'line-center',
      'text-anchor': 'center',
      'text-offset': [0, -1.5],
    },
    paint: {
      'text-color': '#FFFFFF',
      'text-halo-color': '#000000',
      'text-halo-width': 1.5,
      'text-opacity': [
        'case',
        ['boolean', ['feature-state', 'selected'], false],
        1,
        0.7,
      ],
    },
  });

  // Registrar event handlers
  registerEventHandlers(map);

  console.log('[RoutesLayer] ✓ Initialized');
}

/**
 * Registra event handlers para interacción con rutas
 *
 * @param map - Instancia de MapLibre
 */
function registerEventHandlers(map: MapLibreMap): void {
  // Hover: cambiar cursor
  map.on('mouseenter', LAYER_LINE_ID, () => {
    map.getCanvas().style.cursor = 'pointer';
  });

  map.on('mouseleave', LAYER_LINE_ID, () => {
    map.getCanvas().style.cursor = '';
  });

  // Click: seleccionar ruta
  map.on('click', LAYER_LINE_ID, (e) => {
    if (!e.features || e.features.length === 0) return;

    const feature = e.features[0];
    const routeId = feature.id as string;

    console.log('[RoutesLayer] Route clicked:', routeId);

    // Dispatch evento personalizado
    window.dispatchEvent(
      new CustomEvent('waste-route-select', {
        detail: {
          routeId,
          route: feature.properties,
        },
      })
    );
  });
}

/**
 * Actualiza los datos de rutas en el mapa
 *
 * @param routes - Array de rutas a mostrar
 *
 * @remarks
 * Actualiza la source con los nuevos datos.
 * Las features mantienen su feature-state (selección).
 */
export function updateRoutesData(routes: WasteRoute[]): void {
  console.log('[RoutesLayer] 🔄 updateRoutesData called with', routes.length, 'routes');

  if (!mapInstance) {
    console.warn('[RoutesLayer] ⚠️ Map not initialized - cannot update routes');
    return;
  }

  console.log('[RoutesLayer] ✅ Map instance available');

  const source = mapInstance.getSource(SOURCE_ID) as GeoJSONSource;
  if (!source) {
    console.warn('[RoutesLayer] ⚠️ Source not found:', SOURCE_ID);
    console.warn('[RoutesLayer] Available sources:', Object.keys(mapInstance.getStyle()?.sources || {}));
    return;
  }

  console.log('[RoutesLayer] ✅ Source found:', SOURCE_ID);

  const featureCollection = routesToFeatureCollection(routes);

  console.log('[RoutesLayer] 📊 Updating data with', featureCollection.features.length, 'features');
  console.log('[RoutesLayer] Features:', featureCollection.features.map(f => ({ id: f.id, code: f.properties?.code })));

  source.setData(featureCollection);
}

/**
 * Resalta una ruta específica
 *
 * @param routeId - ID de la ruta a resaltar
 *
 * @remarks
 * Usa feature-state para aplicar estilos de selección sin modificar los datos.
 */
export function highlightRoute(routeId: string | null): void {
  if (!mapInstance) return;

  // Limpiar selección anterior
  if (selectedRouteId) {
    mapInstance.removeFeatureState({
      source: SOURCE_ID,
      id: selectedRouteId,
    });
  }

  // Aplicar nueva selección
  if (routeId) {
    mapInstance.setFeatureState(
      {
        source: SOURCE_ID,
        id: routeId,
      },
      { selected: true }
    );

    console.log('[RoutesLayer] Highlighted route:', routeId);
  }

  selectedRouteId = routeId;
}

/**
 * Limpia la selección de rutas
 */
export function clearSelection(): void {
  highlightRoute(null);
}

/**
 * Hace zoom a una ruta específica
 *
 * @param geometry - Geometría LineString de la ruta
 * @param padding - Padding en píxeles (default: 50)
 */
export function fitBoundsToRoute(geometry: LineString, padding = 50): void {
  if (!mapInstance) return;

  const coordinates = geometry.coordinates as [number, number][];

  if (coordinates.length === 0) return;

  // Calcular bounding box
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;

  coordinates.forEach(([lng, lat]) => {
    minLng = Math.min(minLng, lng);
    minLat = Math.min(minLat, lat);
    maxLng = Math.max(maxLng, lng);
    maxLat = Math.max(maxLat, lat);
  });

  // Aplicar fitBounds
  mapInstance.fitBounds(
    [
      [minLng, minLat],
      [maxLng, maxLat],
    ],
    {
      padding,
      maxZoom: 16,
      duration: 1000,
    }
  );

  console.log('[RoutesLayer] Fit bounds to route');
}

/**
 * Muestra u oculta la capa de rutas
 *
 * @param visible - true para mostrar, false para ocultar
 */
export function setLayerVisibility(visible: boolean): void {
  if (!mapInstance) return;

  const visibility = visible ? 'visible' : 'none';

  if (mapInstance.getLayer(LAYER_HALO_ID)) {
    mapInstance.setLayoutProperty(LAYER_HALO_ID, 'visibility', visibility);
  }
  if (mapInstance.getLayer(LAYER_LINE_ID)) {
    mapInstance.setLayoutProperty(LAYER_LINE_ID, 'visibility', visibility);
  }
  if (mapInstance.getLayer(LAYER_ARROWS_ID)) {
    mapInstance.setLayoutProperty(LAYER_ARROWS_ID, 'visibility', visibility);
  }
  if (mapInstance.getLayer(LAYER_LABELS_ID)) {
    mapInstance.setLayoutProperty(LAYER_LABELS_ID, 'visibility', visibility);
  }

  console.log('[RoutesLayer] Visibility:', visibility);
}

/**
 * Limpia la capa de rutas
 *
 * @remarks
 * Elimina todas las layers y la source del mapa.
 * Útil al cambiar de proyecto o desmontar el módulo.
 */
export function cleanupRoutesLayer(): void {
  if (!mapInstance) return;

  console.log('[RoutesLayer] Cleaning up...');

  // Eliminar layers (en orden inverso a la creación)
  if (mapInstance.getLayer(LAYER_LABELS_ID)) {
    mapInstance.removeLayer(LAYER_LABELS_ID);
  }
  if (mapInstance.getLayer(LAYER_ARROWS_ID)) {
    mapInstance.removeLayer(LAYER_ARROWS_ID);
  }
  if (mapInstance.getLayer(LAYER_LINE_ID)) {
    mapInstance.removeLayer(LAYER_LINE_ID);
  }
  if (mapInstance.getLayer(LAYER_HALO_ID)) {
    mapInstance.removeLayer(LAYER_HALO_ID);
  }

  // Eliminar source
  if (mapInstance.getSource(SOURCE_ID)) {
    mapInstance.removeSource(SOURCE_ID);
  }

  // Reset estado
  mapInstance = null;
  currentProjectId = null;
  selectedRouteId = null;

  console.log('[RoutesLayer] ✓ Cleaned up');
}

/**
 * Obtiene el ID del proyecto actual
 */
export function getCurrentProjectId(): string | null {
  return currentProjectId;
}

/**
 * Verifica si la capa está inicializada
 */
export function isInitialized(): boolean {
  return mapInstance !== null;
}
